import { useState, useEffect, useMemo, useRef } from "react";
import { createClient } from "../../lib/supabase/client";
import { ChatWidget } from "../ChatWidget";
import { MessageSquare, Users } from "lucide-react";
import "./admin-chat-dashboard.css";
import { authTokens } from "../../services/services";
import { ITokenPayload, IUser } from "../../models/user";

interface Conversation {
  userId: string;
  userName: string;
  lastMessage: string;
  lastMessageAt: string;
  totalMessages: number;
  userMessages: number;
  unreadMessages: number;
  avatar: string;
  companyName?: string;
}

export function AdminChatDashboard() {
  const supabase = useMemo(() => createClient(), []);
  const [conversations, setConversations] = useState<Conversation[]>([]);
  const [selectedUserId, setSelectedUserId] = useState<string | null>(null);
  const [selectedUserName, setSelectedUserName] = useState<string>("");
  const [selectedUserAvatar, setSelectedUserAvatar] = useState<string>("");
  const [isLoading, setIsLoading] = useState(true);
  const [userInfo, setUserInfo] = useState<ITokenPayload | null>(null);
  const selectedUserIdRef = useRef<string | null>(null);

  useEffect(() => {
    selectedUserIdRef.current = selectedUserId;
  }, [selectedUserId]);

  useEffect(() => {
    async function getUserInfo() {
      const user = await authTokens.getUser();
      user && setUserInfo(user);
    }

    getUserInfo();
  }, []);

  const loadConversations = async () => {
    setIsLoading(true);

    const { data, error } = await supabase
      .from("messages")
      .select(
        "user_id, sender_name, sender_avatar, content, created_at, is_from_admin, company_name"
      )
      .order("created_at", { ascending: false });

    if (error) {
      console.error("Error loading conversations:", error);
      setIsLoading(false);
      return;
    }

    // Group by user_id
    const conversationsMap = new Map<string, Conversation>();

    data?.forEach(msg => {
      if (!conversationsMap.has(msg.user_id)) {
        conversationsMap.set(msg.user_id, {
          userId: msg.user_id,
          avatar: msg.sender_avatar,
          userName: msg.is_from_admin ? "Unknown User" : msg.sender_name,
          lastMessage: msg.content,
          lastMessageAt: msg.created_at,
          totalMessages: 1,
          userMessages: msg.is_from_admin ? 0 : 1,
          unreadMessages: 0,
          companyName: msg.company_name
        });
      } else {
        const conv = conversationsMap.get(msg.user_id)!;
        conv.totalMessages++;
        if (!msg.is_from_admin) {
          conv.userMessages++;
          // Update userName nếu chưa có
          if (conv.userName === "Unknown User") {
            conv.userName = msg.sender_name;
          }
          // Update companyName nếu chưa có
          if (!conv.companyName && msg.company_name) {
            conv.companyName = msg.company_name;
          }
        }
      }
    });

    // Tính toán số tin nhắn chưa đọc cho mỗi cuộc trò chuyện
    const conversationEntries = Array.from(conversationsMap.entries());
    for (let i = 0; i < conversationEntries.length; i++) {
      const [userId, conv] = conversationEntries[i];
      const lastReadKey = `admin_chat_last_read_${userId}`;
      const lastReadTime = localStorage.getItem(lastReadKey);

      if (!lastReadTime) {
        // Nếu chưa bao giờ đọc, tất cả tin nhắn của user đều chưa đọc
        conv.unreadMessages = conv.userMessages;
      } else {
        // Đếm tin nhắn của user gửi sau thời gian đọc cuối cùng
        const lastReadDate = new Date(lastReadTime);
        const { data: messagesData } = await supabase
          .from("messages")
          .select("created_at, is_from_admin")
          .eq("user_id", userId)
          .gte("created_at", lastReadTime);

        conv.unreadMessages =
          messagesData?.filter(msg => !msg.is_from_admin).length || 0;
      }
    }

    // Convert to array and sort by last message
    const conversationsArray = Array.from(conversationsMap.values()).sort(
      (a, b) =>
        new Date(b.lastMessageAt).getTime() -
        new Date(a.lastMessageAt).getTime()
    );

    setConversations(conversationsArray);
    setIsLoading(false);
  };

  useEffect(() => {
    loadConversations();

    // Subscribe to new messages
    const channel = supabase
      .channel("admin-messages-list")
      .on(
        "postgres_changes",
        {
          event: "INSERT",
          schema: "public",
          table: "messages"
        },
        payload => {
          const newMessage = payload.new as any;

          // Cập nhật danh sách cuộc trò chuyện realtime
          setConversations(prev => {
            const existingConvIndex = prev.findIndex(
              c => c.userId === newMessage.user_id
            );

            if (existingConvIndex >= 0) {
              // Conversation đã tồn tại - cập nhật
              const updatedConversations = [...prev];
              const conv = { ...updatedConversations[existingConvIndex] };

              // Cập nhật last message và time
              conv.lastMessage = newMessage.content;
              conv.lastMessageAt = newMessage.created_at;
              conv.totalMessages++;

              // Cập nhật companyName nếu chưa có
              if (!conv.companyName && newMessage.company_name) {
                conv.companyName = newMessage.company_name;
              }

              // Xử lý unread messages
              if (!newMessage.is_from_admin) {
                conv.userMessages++;
                // Nếu là conversation đang được chọn, giữ unreadMessages = 0
                if (newMessage.user_id === selectedUserIdRef.current) {
                  conv.unreadMessages = 0;
                } else {
                  // Nếu không phải conversation đang chọn, tăng unreadMessages
                  conv.unreadMessages++;
                }
              }

              // Xóa conversation cũ và thêm lên đầu
              updatedConversations.splice(existingConvIndex, 1);
              return [conv, ...updatedConversations];
            } else {
              // Conversation mới - thêm vào đầu danh sách
              const newConv: Conversation = {
                userId: newMessage.user_id,
                userName: newMessage.is_from_admin
                  ? "Unknown User"
                  : newMessage.sender_name,
                avatar: newMessage.sender_avatar,
                lastMessage: newMessage.content,
                lastMessageAt: newMessage.created_at,
                totalMessages: 1,
                userMessages: newMessage.is_from_admin ? 0 : 1,
                unreadMessages: newMessage.is_from_admin ? 0 : 1,
                companyName: newMessage.company_name
              };
              return [newConv, ...prev];
            }
          });
        }
      )
      .subscribe();

    return () => {
      supabase.removeChannel(channel);
    };
  }, [supabase]);

  const handleSelectConversation = (
    userId: string,
    userName: string,
    avatar: string
  ) => {
    setSelectedUserId(userId);
    setSelectedUserName(userName);
    setSelectedUserAvatar(avatar);

    // Đánh dấu cuộc trò chuyện đã đọc
    const lastReadKey = `admin_chat_last_read_${userId}`;
    localStorage.setItem(lastReadKey, new Date().toISOString());

    // Cập nhật số tin nhắn chưa đọc về 0
    setConversations(prev =>
      prev.map(conv =>
        conv.userId === userId ? { ...conv, unreadMessages: 0 } : conv
      )
    );
  };

  return (
    <div className="admin-chat-dashboard">
      {/* Left Panel - Conversations List */}
      <div className="conversations-panel">
        <div className="conversations-header">
          <Users size={20} />
          <h2>Customer Support</h2>
        </div>

        {isLoading ? (
          <div className="conversations-loading">
            <p>Loading conversations...</p>
          </div>
        ) : conversations.length === 0 ? (
          <div className="conversations-empty">
            <MessageSquare size={48} />
            <p>No conversations yet</p>
            <p className="text-muted">
              Conversations will appear here when users start chatting
            </p>
          </div>
        ) : (
          <div className="conversations-list">
            {conversations.map(conv => (
              <div
                key={conv.userId}
                onClick={() =>
                  handleSelectConversation(
                    conv.userId,
                    conv.userName,
                    conv.avatar
                  )
                }
                className={`conversation-item ${
                  selectedUserId === conv.userId ? "active" : ""
                }`}
              >
                <div className="conversation-avatar">
                  {/* <img />
                   */}
                  {conv.userName.charAt(0).toUpperCase()}
                </div>
                <div className="conversation-content">
                  <div style={{ marginBottom: "10px" }}>
                    <div
                      style={{ margin: "0" }}
                      className="conversation-header"
                    >
                      <span className="conversation-name">{conv.userName}</span>
                      <span className="conversation-time">
                        {new Date(conv.lastMessageAt).toLocaleTimeString(
                          "vi-VN",
                          {
                            hour: "2-digit",
                            minute: "2-digit"
                          }
                        )}
                      </span>
                    </div>
                    <div className="conversation-company">
                      <span style={{ fontSize: "12px", color: "#000000" }}>
                        {conv.companyName || "Unknown Company"}
                      </span>
                    </div>
                  </div>
                  <div className="conversation-preview">
                    <span className="conversation-last-message">
                      {conv.lastMessage.length > 50
                        ? conv.lastMessage.substring(0, 50) + "..."
                        : conv.lastMessage}
                    </span>
                    {/* Chỉ hiển thị badge nếu có tin nhắn chưa đọc VÀ không phải conversation đang chọn */}
                    {conv.unreadMessages > 0 &&
                      selectedUserId !== conv.userId && (
                        <span className="conversation-badge">
                          {conv.unreadMessages}
                        </span>
                      )}
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Right Panel - Chat Window */}
      <div className="chat-panel">
        {selectedUserId ? (
          <div className="chat-window">
            <div className="chat-window-header">
              <div className="chat-window-user">
                <div className="chat-window-avatar">
                  {/* {selectedUserAvatar ? (
                    <img src={selectedUserAvatar} alt={selectedUserName} style={{ width: '100%', height: '100%', objectFit: 'cover', borderRadius: '50%' }} />
                  ) : (
                    selectedUserName.charAt(0).toUpperCase()
                  )} */}
                  {selectedUserName.charAt(0).toUpperCase()}
                </div>
                <div>
                  <h3>{selectedUserName}</h3>
                  <p className="text-muted">Customer</p>
                </div>
              </div>
            </div>
            <div className="chat-window-content">
              <ChatWidget
                userId={selectedUserId}
                username="Support Team"
                userAvatar={userInfo?.avatar}
                isAdmin={true}
                embedded={true}
                isShowHeader={false}
              />
            </div>
          </div>
        ) : (
          <div className="chat-empty">
            <MessageSquare size={64} />
            <h3>Chọn một cuộc trò chuyện</h3>
            <p>Chọn một cuộc trò chuyện từ danh sách để bắt đầu trò chuyện</p>
          </div>
        )}
      </div>
    </div>
  );
}
